import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { SectionTitle } from "@/components/dashboard/widgets";
import { MessageSquare, Mail, ArrowUpRight, Check, X, Loader2, Bot } from "lucide-react";

/**
 * Replies inbox (iter78). Single home for every triaged reply from a lead.
 * Buyer can 1-click approve a suggested action or dismiss.
 */
const INTENT_META = {
  confirmed_hot: { label: "Hot",          stripe: "bg-[#0F172A]" },
  reschedule:    { label: "Reschedule",   stripe: "bg-[#0055FF]" },
  question:      { label: "Question",     stripe: "bg-slate-400" },
  disqualify:    { label: "Disqualify",   stripe: "bg-rose-500" },
  unsubscribe:   { label: "Unsubscribe",  stripe: "bg-slate-300" },
};

const STATUS_META = {
  pending:       { label: "Pending",      tone: "text-slate-500" },
  suggested:     { label: "Suggested",    tone: "text-[#0055FF]" },
  auto_actioned: { label: "Auto-actioned",tone: "text-emerald-700" },
  approved:      { label: "Approved",     tone: "text-emerald-700" },
  dismissed:     { label: "Dismissed",    tone: "text-slate-400" },
  needs_review:  { label: "Review",       tone: "text-amber-600" },
  orphan_no_lead: { label: "Orphan",      tone: "text-slate-400" },
};

export default function Replies() {
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("suggested");
  const [data, setData] = useState({ items: [], counts: {} });
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const url = filter === "all" ? "/client/replies?limit=100" : `/client/replies?status=${filter}&limit=100`;
      const r = await api.get(url);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load replies");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { setLoading(true); load(); /* eslint-disable-next-line */ }, [filter]);

  const approve = async (rid) => {
    setBusy(rid);
    try {
      const r = await api.post(`/client/replies/${rid}/approve`);
      toast.success(r.data?.action_taken || "Approved");
      setActive(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally {
      setBusy(null);
    }
  };

  const dismiss = async (rid) => {
    setBusy(rid);
    try {
      await api.post(`/client/replies/${rid}/dismiss`);
      toast.success("Dismissed");
      setActive(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dismiss failed");
    } finally {
      setBusy(null);
    }
  };

  const filters = [
    { key: "suggested",     label: "Suggested",     n: data.counts?.suggested },
    { key: "auto_actioned", label: "Auto-actioned", n: data.counts?.auto_actioned },
    { key: "needs_review",  label: "Needs review",  n: data.counts?.needs_review },
    { key: "approved",      label: "Approved",      n: data.counts?.approved },
    { key: "dismissed",     label: "Dismissed",     n: data.counts?.dismissed },
    { key: "all",           label: "All",           n: null },
  ];

  return (
    <div className="space-y-6">
      <SectionTitle
        eyebrow="AI · addon"
        title="Replies inbox"
        subtitle="Every lead reply triaged by Claude. Approve a suggestion in 1 click, or let auto-action handle it for you (configure in Auto Follow-Up)."
      />

      <div className="flex items-center gap-1 border border-slate-200 rounded-md overflow-x-auto" role="tablist">
        {filters.map((f, i) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-2 text-[12px] whitespace-nowrap transition-colors ${filter === f.key ? "bg-[#0F172A] text-white" : "bg-white text-slate-700 hover:bg-slate-50"} ${i > 0 ? "border-l border-slate-200" : ""}`}
            data-testid={`replies-filter-${f.key}`}
          >
            {f.label}
            {typeof f.n === "number" && f.n > 0 && (
              <span className={`ml-1.5 tabular-nums ${filter === f.key ? "text-white/70" : "text-slate-500"}`}>· {f.n}</span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <Skeleton className="h-64 w-full" />
      ) : (data.items || []).length === 0 ? (
        <Card className="rounded-xl border-slate-200 shadow-soft">
          <CardContent className="p-12 text-center">
            <div className="inline-grid place-items-center size-12 rounded-md bg-slate-100 text-slate-500 mx-auto mb-4">
              <Bot className="size-5" />
            </div>
            <h3 className="text-[15px] font-semibold text-slate-900">No replies in this view</h3>
            <p className="text-[12.5px] text-slate-500 mt-1 max-w-md mx-auto leading-relaxed">
              Once your leads reply to an Auto Follow-Up message, Claude triages them here. Enable Auto Follow-Up + Reply Triage to start the flow.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="rounded-xl border-slate-200 shadow-soft overflow-hidden" data-testid="replies-list">
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead className="bg-slate-50/70 border-b border-slate-200">
                <tr className="text-left text-[10.5px] uppercase tracking-[0.1em] text-slate-500 font-semibold">
                  <th className="py-3 px-4">When</th>
                  <th className="py-3 px-4">Lead</th>
                  <th className="py-3 px-4">Intent</th>
                  <th className="py-3 px-4">Channel</th>
                  <th className="py-3 px-4">Confidence</th>
                  <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {(data.items || []).map((r, i) => {
                const im = INTENT_META[r.intent] || { label: r.intent || "—", stripe: "bg-slate-300" };
                const sm = STATUS_META[r.triage_status] || { label: r.triage_status, tone: "text-slate-500" };
                const dt = new Date(r.received_at);
                return (
                  <tr key={r.id} className={`${i % 2 === 0 ? "bg-white" : "bg-slate-50/40"} hover:bg-[#0055FF]/[0.02] cursor-pointer relative`} onClick={() => setActive(r)} data-testid={`reply-row-${r.id}`}>
                    <td className="py-3 px-4 text-slate-700 tabular-nums whitespace-nowrap"><span className={`absolute left-0 top-0 bottom-0 w-[2px] ${im.stripe}`} />{dt.toLocaleDateString()} <span className="text-slate-400">{dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span></td>
                    <td className="py-3 px-4 text-slate-900 font-medium truncate max-w-[180px]" title={r.from_email || r.from_phone}>{r.from_email || r.from_phone || "—"}</td>
                    <td className="py-3 px-4"><span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-slate-800"><span className={`size-1.5 rounded-full ${im.stripe}`} />{im.label}</span></td>
                    <td className="py-3 px-4 text-slate-700">{r.channel === "sms" ? <MessageSquare className="size-3 inline -mt-0.5 mr-1" /> : <Mail className="size-3 inline -mt-0.5 mr-1" />}{r.channel}</td>
                    <td className="py-3 px-4 text-slate-700 tabular-nums">{r.confidence ? `${Math.round(r.confidence * 100)}%` : "—"}</td>
                    <td className={`py-3 px-4 ${sm.tone} font-medium text-[11.5px]`}>{sm.label}</td>
                    <td className="py-3 px-4 text-slate-400"><ArrowUpRight className="size-3" /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </Card>
      )}

      {/* Detail drawer */}
      <Sheet open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <SheetContent className="sm:max-w-lg w-full">
          {active && (
            <>
              <SheetHeader className="space-y-1.5">
                <div className="text-[10.5px] uppercase tracking-[0.14em] text-slate-500 font-semibold">Reply · {INTENT_META[active.intent]?.label || "—"}</div>
                <SheetTitle className="text-[16px]">{active.from_email || active.from_phone}</SheetTitle>
                <p className="text-[12px] text-slate-500">Received {new Date(active.received_at).toLocaleString()} · {active.channel}</p>
              </SheetHeader>

              <div className="mt-5 space-y-5">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.12em] font-semibold text-slate-500 mb-1.5">Reply text</div>
                  <div className="rounded-md border border-slate-200 bg-slate-50/50 p-3 text-[13px] text-slate-900 whitespace-pre-wrap leading-relaxed font-mono">{active.body}</div>
                </div>

                {active.dq_reason && (
                  <div className="rounded-md border border-rose-200 bg-rose-50/50 p-3 text-[12.5px] text-rose-900">
                    <div className="text-[10px] uppercase tracking-[0.12em] font-semibold text-rose-700 mb-0.5">DQ reason</div>
                    {active.dq_reason}
                  </div>
                )}
                {active.callback_iso && (
                  <div className="rounded-md border border-[#0055FF]/20 bg-[#0055FF]/5 p-3 text-[12.5px] text-[#0F172A]">
                    <div className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#0055FF] mb-0.5">Requested callback</div>
                    {new Date(active.callback_iso).toLocaleString()}
                  </div>
                )}

                {active.suggested_response && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.12em] font-semibold text-slate-500 mb-1.5">Suggested response</div>
                    <div className="rounded-md border border-slate-200 bg-white p-3 text-[13px] text-slate-900 leading-relaxed">{active.suggested_response}</div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-2 text-[11.5px]">
                  <div><div className="text-[9.5px] uppercase tracking-[0.12em] text-slate-500 font-semibold">Confidence</div><div className="text-slate-900 font-semibold tabular-nums mt-0.5">{active.confidence ? `${Math.round(active.confidence * 100)}%` : "—"}</div></div>
                  <div><div className="text-[9.5px] uppercase tracking-[0.12em] text-slate-500 font-semibold">Hot score</div><div className="text-slate-900 font-semibold tabular-nums mt-0.5">{active.hot_score ? `${Math.round(active.hot_score * 100)}%` : "—"}</div></div>
                </div>

                {["suggested", "needs_review"].includes(active.triage_status) && (
                  <div className="flex items-center gap-2 pt-2">
                    <Button onClick={() => approve(active.id)} disabled={busy === active.id} className="flex-1 rounded-md bg-[#0F172A] hover:bg-[#1e293b] text-white h-9 text-[13px]" data-testid="reply-approve">
                      {busy === active.id ? <Loader2 className="size-3.5 animate-spin" /> : <Check className="size-3.5" />} Approve & action
                    </Button>
                    <Button onClick={() => dismiss(active.id)} disabled={busy === active.id} variant="outline" className="rounded-md h-9 text-[13px]" data-testid="reply-dismiss">
                      <X className="size-3.5" /> Dismiss
                    </Button>
                  </div>
                )}
                {active.action_taken && (
                  <div className="text-[11.5px] text-slate-500 italic">Action taken: {active.action_taken}</div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
